import { ref } from 'vue'
import { convertDateToAge } from '@/utils/date.utils'
import AdditionalCoverage from '@/modules/common/common/models/APP/AdditionalCoverage'
import type CoverageBasic from '@/modules/common/common/models/APP/CoverageBasic'
import type Insured from '@/modules/common/common/models/APP/Insured'
import type LifeConfigurationCapitalOption from '../models/APP/LifeConfigurationCapitalOption'
import { validateActuarialDate } from '@/utils/validations.utils'

export function useCoverages() {
  const mandatoryCoverages = ref<CoverageBasic[]>([])
  const optionalCoverages = ref<CoverageBasic[]>([])
  const mandatoryToSave = ref<AdditionalCoverage[]>([])
  const optionalToSave = ref<AdditionalCoverage[]>([])

  const mandatoryCoverageSlider = ref<{ min: number | null; max: number | null; step: number }>({
    min: null,
    max: null,
    step: 100
  })

  function getSliderCapitals(coverage: CoverageBasic) {
    return coverage.coverageInfoCapitals.map((c) => Number(c.sumInsured))
  }

  function getMandatoryCapitals(
    insured: Insured,
    capitalOptions: LifeConfigurationCapitalOption[]
  ) {
    const insuranceBirthDate = insured.userDetails.birthDate

    if (!insuranceBirthDate) {
      return { min: null, max: null }
    }

    const age = convertDateToAge(insuranceBirthDate)
    const option = capitalOptions.find((a) => {
      const isOlder = age > a.age && a.older
      const isYounger = age < a.age && !a.older
      const isEquals = age === a.age && a.equals

      return isOlder || isYounger || isEquals
    })

    return { min: option?.min ?? null, max: option?.max ?? null }
  }

  function loadMandatoryCapitals(
    insured: Insured,
    capitalOptions: LifeConfigurationCapitalOption[]
  ) {
    const { min, max } = getMandatoryCapitals(insured, capitalOptions)
    mandatoryCoverageSlider.value.min = min
    mandatoryCoverageSlider.value.max = max
  }

  async function loadCoverages(
    storeCoverages: CoverageBasic[],
    insured: Insured,
    capitalOptions: LifeConfigurationCapitalOption[],
    coveragesCapital?: number
  ) {
    try {
      const coverages = filterAvailableCoverages(storeCoverages, insured)

      loadMandatoryCapitals(insured, capitalOptions)

      mandatoryCoverages.value = coverages.filter((c) => c.mandatory)
      optionalCoverages.value = coverages.filter((c) => !c.mandatory)

      mandatoryToSave.value = mandatoryCoverages.value.map((c) =>
        convert2AdditionalCoverage(c, insured.additionalCoverages, coveragesCapital)
      )
      optionalToSave.value = optionalCoverages.value.map((c) =>
        convert2AdditionalCoverage(c, insured.additionalCoverages, coveragesCapital)
      )
    } catch (error) {
      console.error(error)
    }
  }

  function filterAvailableCoverages(coverages: CoverageBasic[], insured: Insured): CoverageBasic[] {
    const insuredBirthDate = insured.userDetails.birthDate
    if (!insuredBirthDate) return coverages

    const newInsuredBirthDate = new Date(insuredBirthDate)
    return coverages.filter((coverage) => {
      const { minAge, maxAge } = coverage
      return validateActuarialDate(newInsuredBirthDate, minAge, maxAge)
    })
  }

  function convert2AdditionalCoverage(
    coverage: CoverageBasic,
    savedCoverages?: AdditionalCoverage[],
    coveragesCapital?: number
  ): AdditionalCoverage {
    const key = coverage.coverageCode
    const savedCoverage = getSavedCoverage(key, savedCoverages ?? [])

    const checked = savedCoverage?.checked ?? coverage.mandatory

    let value
    if (coverage.coverageInfoCapitals.length) {
      const capitals = coverage.coverageInfoCapitals.map((c) => Number(c.sumInsured))
      const defaultCapitalOption = Math.min(...capitals)
      value = savedCoverage?.value ?? defaultCapitalOption
    } else {
      value = savedCoverage?.value ?? coveragesCapital ?? null
    }

    return new AdditionalCoverage({ key, checked, value })
  }

  function getSavedCoverage(coverageCode: string, savedCoverages: AdditionalCoverage[] = []) {
    return savedCoverages.find((c) => c.key === coverageCode)
  }

  return {
    mandatoryCoverageSlider,
    mandatoryCoverages,
    optionalCoverages,
    mandatoryToSave,
    optionalToSave,

    getSliderCapitals,
    loadMandatoryCapitals,
    loadCoverages
  }
}
